import React, { Component } from 'react';
import { ChevronRight } from 'react-feather';
import { Input } from '@components-shared/configConstructor/Input';
import { fetchApi } from 'api/fetchApi';
import cn from 'classnames';
import { AsyncSelect } from 'components/shared/configConstructor/asyncSelect';
import { GENERAL_CONSTANTS } from 'components/shared/general.constants';
import { EmptyObject } from 'components/shared/shared.interfaces';
import { merge } from 'lodash-es';
import { observer } from 'mobx-react';
import { mobxStore } from 'store/mobx';
import { handleError } from 'utils/errors';
import { CcDataType } from '.';
import { CronExpressionEditor } from './cronExpressionEditor';
import DatetimePicker from './datetimepicker';
import DynamicDropdown from './dynamicDropdown';
import DynamicStringDropdown from './dynamicStringDropdown';
import FileSourceInput from './fileSourceInput';
import InputsTable from './inputsTable';
import PhoneInput from './phoneInputConstructorForm';
import { Select } from './select';
import StringSet from './set';
import StringMap from './stringMap';
import { Attributes, EventTarget, Option, InputsTableValues, AsyncSelectSettings, ISelectOption } from './types';
import {
  isInteger,
  isFloatingPointNumber,
  isNullableInteger,
  orderValue,
  isNullableFloatingPointNumber,
} from './utils';

type FieldValue = string | number | boolean | Array<InputsTableValues> | InputsTableValues | Array<string> | null;

interface IProps<K> {
  attributes?: Attributes;
  data: CcDataType<K> | EmptyObject;
  name: string;
  members: Array<Option>;
  getter: Array<string>;
  changeForm?: (data: CcDataType<K> | EmptyObject) => void;
  disabledForm?: boolean;
  defaultExpanded?: boolean;
  hasAccessToGetUnmasked?: boolean;
}

interface IState {
  expanded: boolean;
}

class Fieldset<K> extends Component<IProps<K>, IState> {
  static get(obj: Record<string, any>, path: Array<string>) {
    return path.reduce((xs, x) => (xs && xs[x] != null ? xs[x] : null), obj);
  }

  state = {
    expanded: this.getExpandedAttributes(),
  };

  get sortedMembers() {
    return (this.props.members || []).sort((a, b) => orderValue(a) - orderValue(b));
  }

  mobxAccountStore = mobxStore.accountStore;

  mobxAppSettingStore = mobxStore.appSettingStore;

  getExpandedAttributes() {
    const { attributes = {} } = this.props;
    const { expanded = true } = attributes;

    return expanded;
  }

  onChange = (e: EventTarget, map?: Record<string, string>) => {
    const { name, value, checked, type } = e.target;
    const { data, getter, changeForm } = this.props;

    // @ts-ignore
    const part = Fieldset.get(data, getter);

    part[name] = type === 'checkbox' ? checked : value;

    changeForm && changeForm({ ...data });
    this.replaceErrorMessage(e, map);
  };

  replaceErrorMessage = (e: EventTarget, map?: Record<string, string>) => {
    if (map) {
      Object.keys(map).forEach((errorType) => {
        if (e.target.validity?.[errorType as keyof ValidityState]) {
          e.target.setCustomValidity?.(map[errorType]);
        } else {
          e.target.setCustomValidity?.('');
        }
      });
    }
  };

  toggle = (isExpandable: boolean) => {
    if (!isExpandable) {
      return;
    }

    this.setState((prevState) => ({ expanded: !prevState.expanded }));
  };

  onSetChange = async (
    name: string,
    value: Array<string> | Record<string, string> | ISelectOption,
    callbackUrl?: string,
  ) => {
    const { data, getter, changeForm } = this.props;

    // @ts-ignore
    const part = Fieldset.get(data, getter);

    part[name] = value;

    if (callbackUrl) {
      try {
        const response = await fetchApi.get<ISelectOption[]>(callbackUrl, {
          params: { value: (value as ISelectOption).value },
        });

        changeForm && changeForm(merge(data, response.data));

        return;
      } catch (error) {
        handleError('Failed to fetch options', error);
      }
    }

    changeForm && changeForm({ ...data });
  };

  getLabel = (attributes: Attributes, name: string) => {
    const { i18next } = this.mobxAppSettingStore;
    const { language } = i18next;

    const {
      generalSettings: { brokerParams = {} },
    } = this.mobxAccountStore;

    const lang = this.getLanguage(language);
    let label = name;

    if (attributes && attributes.localization) {
      label = attributes.localization[lang] || attributes.localization.default || name;
    }

    if (label && label.includes('brokerName')) {
      const brokerName = brokerParams[language]?.brokerName;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      label = i18next.t(label as any, { brokerName });
    }

    return label;
  };

  getHint = (attributes: Attributes) => {
    const { i18next } = this.mobxAppSettingStore;
    const { language } = i18next;

    const { hint } = attributes;

    if (!hint) {
      return null;
    }

    const lang = this.getLanguage(language);
    const hintMessage = hint[lang] || hint.default || hint.name;

    return <span className="cc-filed__hint">{hintMessage}</span>;
  };

  getLanguage = (language: string) => {
    return language && language.split('-')[0];
  };

  getJsonPropertyName = ({ attributes, name }: Option) => {
    if (attributes?.jsonProperty?.propertyName) {
      return attributes.jsonProperty.propertyName;
    }

    return name;
  };

  render() {
    const {
      name,
      data,
      getter: parentGetter,
      changeForm,
      attributes = {},
      disabledForm,
      hasAccessToGetUnmasked = false,
    } = this.props;
    const { expanded } = this.state;

    const isExpandable = typeof attributes.expanded !== 'undefined';

    return (
      <div className="cc-fieldset">
        <legend className="cc-fieldset__legend">
          <button
            className={cn('cc-fieldset__toggler', { expanded, isExpandable })}
            type="button"
            onClick={() => this.toggle(isExpandable)}>
            {isExpandable && <ChevronRight className="cc-fieldset__toggler-icon" />}
            <span className="cc-fieldset__legend-name">{this.getLabel(attributes, name)}</span>
          </button>
        </legend>
        {expanded &&
          this.sortedMembers.map((m) => {
            const inputName = this.getJsonPropertyName(m);
            const type = m.type?.toLowerCase();
            const getter = [...parentGetter, inputName];
            // @ts-ignore
            const value = Fieldset.get(data, getter) as FieldValue;
            const required = !m.canBeNull;
            const { pattern, errorMessage } = m?.attributes?.regexConstraint || {};
            const { readOnly, isDisabledForm, cssClass = '' } = m?.attributes || {};
            const disabled = disabledForm || readOnly || false;

            if (type === 'class') {
              return (
                <Fieldset
                  key={m.name}
                  name={inputName}
                  members={m.members}
                  data={data}
                  changeForm={changeForm}
                  hasAccessToGetUnmasked={hasAccessToGetUnmasked}
                  getter={getter}
                  attributes={m.attributes}
                  disabledForm={disabledForm || isDisabledForm}
                />
              );
            }

            if (type === 'bool') {
              const isRequiredField = required && m.attributes && m.attributes.termsAndConditions;

              return (
                <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {isRequiredField && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <label className={`${type}-wrapper ${cssClass}`}>
                    <input
                      type="checkbox"
                      name={inputName}
                      checked={value === true}
                      onChange={this.onChange}
                      required={isRequiredField}
                      disabled={disabled}
                    />
                    <span className={`cc-filed__checkbox ${value}`} />
                  </label>
                </div>
              );
            }

            if (type === 'datetime') {
              return (
                <label className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <DatetimePicker
                    className="cc-input"
                    type="datetime-local"
                    name={inputName}
                    value={value as string}
                    onChange={this.onChange}
                    required={required}
                    attributes={{ ...m.attributes, disabled }}
                  />
                </label>
              );
            }

            const isOfIntegerType = isInteger(type);
            const isOfNullableIntegerType = isNullableInteger(type);

            if (isOfIntegerType || isOfNullableIntegerType) {
              const numericAttributes = m.attributes?.numeric || {};

              return (
                <label className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <input
                    className="cc-input"
                    type="number"
                    pattern="^[0-9]\d*$"
                    name={inputName}
                    value={value as number}
                    onChange={this.onChange}
                    required={required}
                    disabled={disabled}
                    {...numericAttributes}
                  />
                </label>
              );
            }

            if (isFloatingPointNumber(type) || isNullableFloatingPointNumber(type)) {
              const numericAttributes = m.attributes?.numeric || {};

              return (
                <label className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <input
                    className="cc-input"
                    type="number"
                    name={inputName}
                    value={value as number}
                    onChange={this.onChange}
                    required={required}
                    disabled={disabled}
                    {...numericAttributes}
                  />
                </label>
              );
            }

            if (type === 'string') {
              const isMasked = Boolean(m.attributes.maskProperty);
              const canToggleMask = hasAccessToGetUnmasked && isMasked;

              const isDisabled = !hasAccessToGetUnmasked && Boolean(m.attributes.maskProperty);

              if (m.attributes.dynamicDroppdownSetting) {
                return (
                  <div className={`cc-field enum ${type} ${cssClass}`} key={m.name}>
                    <span className="cc-filed__label">
                      {this.getLabel(m.attributes, m.name)}
                      {required && <span className="cc-filed__required">*</span>}
                      {this.getHint(m.attributes)}
                    </span>
                    <DynamicStringDropdown
                      name={inputName}
                      value={value as string}
                      required={required}
                      attributes={m.attributes}
                      onChange={this.onChange}
                      onSetChange={this.onSetChange}
                      getLabel={this.getLabel}
                      disabled={disabled}
                    />
                  </div>
                );
              } else {
                return (
                  <Input
                    key={m.name}
                    name={inputName}
                    pattern={pattern}
                    disabled={isDisabled || disabled}
                    onChange={this.onChange}
                    required={required}
                    value={value as string}
                    label={this.getLabel(m.attributes, m.name)}
                    errorMessage={errorMessage}
                    hint={this.getHint(m.attributes)}
                    maskable={isMasked}
                    canToggleMask={canToggleMask}
                    cssClass={cssClass}
                  />
                );
              }
            }

            if (type === 'textarea') {
              return (
                <label className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <textarea
                    className="cc-input"
                    name={inputName}
                    value={(value === null ? '' : value) as string}
                    onChange={this.onChange}
                    required={required}
                    disabled={disabled}
                  />
                </label>
              );
            }

            if (type === 'phoneinput') {
              return (
                <label className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <PhoneInput
                    className="cc-input"
                    name={inputName}
                    onChange={this.onChange}
                    required={required}
                    value={value as string}
                    disabled={disabled}
                    preferredCountries={GENERAL_CONSTANTS.PREFERRED_COUNTRIES}
                    defaultCountry={GENERAL_CONSTANTS.DEFAULT_COUNTRY}
                  />
                </label>
              );
            }

            if (type === 'html') {
              return (
                <div
                  key={m.name}
                  className={`cc-field html ${cssClass}`}
                  dangerouslySetInnerHTML={{ __html: this.getLabel(m.attributes, m.name) }}
                />
              );
            }

            if (type === 'filesource') {
              return (
                <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <FileSourceInput
                    key={m.name}
                    className="cc-input"
                    name={inputName}
                    value={value as string}
                    attributes={m.attributes}
                    onChange={this.onChange}
                    required={required}
                    disabled={disabled}
                  />
                </div>
              );
            }

            if (m.type === 'set' || m.type === 'array') {
              if (m.value?.type === 'class') {
                return (
                  <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                    <span className="cc-filed__label">
                      {this.getLabel(m.attributes, m.name)}
                      {required && <span className="cc-filed__required">*</span>}
                      {this.getHint(m.attributes)}
                    </span>
                    <InputsTable
                      name={inputName}
                      value={(value || []) as Array<InputsTableValues>}
                      onChange={this.onChange}
                      members={m}
                      attributes={m.attributes}
                      getLabel={this.getLabel}
                      disabled={disabled}
                    />
                  </div>
                );
              }

              return (
                <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <StringSet
                    name={inputName}
                    value={(value || []) as Array<string>}
                    onChange={this.onSetChange}
                    disabled={disabled}
                  />
                </div>
              );
            }

            if (type === 'map') {
              return (
                <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <StringMap
                    onChange={this.onSetChange}
                    name={inputName}
                    value={(value || []) as Record<string, string>}
                    disabled={disabled}
                  />
                </div>
              );
            }

            if (type === 'enum') {
              return (
                <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <Select
                    name={inputName}
                    attributes={m.attributes}
                    values={m.values}
                    value={(value || '') as string}
                    required={required}
                    onChange={this.onChange}
                    getLabel={this.getLabel}
                    disabled={disabled}
                  />
                </div>
              );
            }

            if (type === 'asyncselect') {
              return (
                <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <AsyncSelect
                    disabled={disabled}
                    name={inputName}
                    onSetChange={this.onSetChange}
                    value={value || undefined}
                    required={required}
                    {...(m.attributes.asyncSelectSettings as AsyncSelectSettings)}
                    style={{ width: '100%' }}
                  />
                </div>
              );
            }

            if (type === 'dynamicdropdown') {
              return (
                <div className={`cc-field enum ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <DynamicDropdown
                    name={inputName}
                    value={value as string}
                    required={required}
                    attributes={m.attributes}
                    onChange={this.onChange}
                    onSetChange={this.onSetChange}
                    getLabel={this.getLabel}
                    disabled={disabled}
                  />
                </div>
              );
            }

            if (type === 'cronexpression') {
              return (
                <div className={`cc-field ${type} ${cssClass}`} key={m.name}>
                  <span className="cc-filed__label">
                    {this.getLabel(m.attributes, m.name)}
                    {required && <span className="cc-filed__required">*</span>}
                    {this.getHint(m.attributes)}
                  </span>
                  <CronExpressionEditor
                    name={inputName}
                    value={value as string}
                    onChange={this.onChange}
                    disabled={disabled}
                  />
                </div>
              );
            }

            return (
              <label className={`cc-field ${type} ${cssClass}`} key={m.name}>
                <span className="cc-filed__label">
                  {this.getLabel(m.attributes, m.name)}
                  {required && <span className="cc-filed__required">*</span>}
                  {this.getHint(m.attributes)}
                </span>{' '}
                - {type}
              </label>
            );
          })}
      </div>
    );
  }
}

export default observer(Fieldset);
